import React, { useEffect } from "react";
import { AiOutlineClose } from "react-icons/ai";

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - form: { report_title, subtitle, address, pin_code, landmark, mobile, email, website, logo_align }
 *  - logoPreviewUrl: string | null
 *  - letterPreviewUrl: string | null
 */
 function PreviewModal({ open, onClose, form = {}, logoPreviewUrl, letterPreviewUrl }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const {
    report_title = "",
    subtitle = "",
    address = "",
    pin_code = "",
    landmark = "",
    mobile = "",
    email = "",
    website = "",
    logo_align = "Left",
  } = form;

  const renderHeader = () => {
    if (letterPreviewUrl) return null; // letterhead image shown separately

    const headerText = (
      <div className="flex flex-col gap-0.5">
        <div className="text-2xl font-extrabold">{report_title || "Company Name"}</div>
        {subtitle && <div className="text-sm -mt-0.5">{subtitle}</div>}
      </div>
    );

    if (logoPreviewUrl && logo_align === "Left") {
      return (
        <div className="flex items-center gap-4">
          <div className="w-28 h-20 rounded-md overflow-hidden bg-white border border-gray-200 flex items-center justify-center">
            <img src={logoPreviewUrl} alt="logo" className="max-w-full max-h-full object-contain" />
          </div>
          <div className="flex-1">{headerText}</div>
        </div>
      );
    }

    if (logoPreviewUrl && logo_align === "Right") {
      return (
        <div className="flex items-center gap-4">
          <div className="flex-1">{headerText}</div>
          <div className="w-28 h-20 rounded-md overflow-hidden bg-white border border-gray-200 flex items-center justify-center">
            <img src={logoPreviewUrl} alt="logo" className="max-w-full max-h-full object-contain" />
          </div>
        </div>
      );
    }

    
    return (
      <div className="text-center">
        <div className="text-2xl font-extrabold">{report_title || "Company Name"}</div>
        {subtitle && <div className="text-sm mt-1">{subtitle}</div>}
      </div>
    );
  };

  const renderContactLine = () => {
    const parts = [];
    if (mobile) parts.push(`Mobile No: ${mobile}`);
    if (email) parts.push(email);
    if (website) parts.push(website);
    return parts.join("  |  ");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Preview"
        className="relative z-10 w-11/12 max-w-3xl bg-white rounded-lg shadow-2xl overflow-hidden"
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close preview"
          className="absolute right-3 top-3 text-gray-700 hover:text-gray-900"
        >
          <AiOutlineClose size={20} />
        </button>

        {/* Content */}
        <div className="p-5 font-sans text-gray-900">
          {/* Letterhead full image if provided */}
          {letterPreviewUrl ? (
            <div className="mb-3">
              <img src={letterPreviewUrl} alt="letterhead" className="w-full h-auto rounded-md object-contain" />
            </div>
          ) : (
            <div className="mb-3">{renderHeader()}</div>
          )}

          {/* Divider */}
          <div className="h-0.5 bg-black my-3" />

          {/* Address and right space */}
          <div className="flex justify-between gap-4">
            <div className="flex-1 text-sm leading-relaxed whitespace-pre-wrap">
              {address || "2nd Floor, Techniplex II, Jn. Veer Savarkar Flyover & S.V. Road, Goregaon(West), Mumbai - 400062"}
              {landmark && <div className="mt-2">Landmark : {landmark}</div>}
            </div>

            <div className="min-w-[140px] text-right text-sm">
              {/* reserved for small right column content (left intentionally blank) */}
            </div>
          </div>

          {/* Contact line */}
          {(mobile || email || website) && (
            <div className="mt-3 text-sm text-gray-800">{renderContactLine()}</div>
          )}

          {/* Spacer + bottom rule */}
          <div className="mt-5 h-1" />
          <div className="h-0.5 bg-black" />

          {/* Footer small info */}
          <div className="mt-3 text-xs text-gray-600">
            {pin_code && <span className="mr-3">Pin: {pin_code}</span>}
            {email && !mobile && <span>{email}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreviewModal;